import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Clock, CheckCircle2, XCircle, PartyPopper, RotateCcw, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useParticipant } from "@/context/ParticipantContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { QUIZ } from "@/constants/testIds";

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function Quiz() {
  const { participant, progress, refreshProgress } = useParticipant();
  const navigate = useNavigate();

  const [stage, setStage] = useState("loading"); // loading | locked | ready | in_progress | result
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionTimeLeft, setQuestionTimeLeft] = useState(60);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const expiredRef = useRef(false);
  const QUESTION_SECONDS = 60;

  const init = useCallback(async () => {
    const p = await refreshProgress();
    if (!p) return;
    if (p.certified) {
      setStage("result");
      setResult({ passed: true, certified: true });
      return;
    }
    if (!p.all_modules_completed) {
      setStage("locked");
      return;
    }
    setStage("ready");
  }, [refreshProgress]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the per-question countdown whenever a new question is shown.
  useEffect(() => {
    if (stage !== "in_progress") return;
    setQuestionTimeLeft(QUESTION_SECONDS);
    expiredRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, stage]);

  // Tick the countdown every second while a question is active.
  useEffect(() => {
    if (stage !== "in_progress") return;
    const id = setInterval(() => {
      setQuestionTimeLeft((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [currentIndex, stage]);

  const startQuiz = async () => {
    setError("");
    try {
      const { data } = await api.post("/quiz/start", { participant_id: participant.id });
      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setAnswers({});
      setCurrentIndex(0);
      setQuestionTimeLeft(QUESTION_SECONDS);
      setStage("in_progress");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const selectAnswer = (qid, opt) => {
    setAnswers((prev) => ({ ...prev, [qid]: opt }));
  };

  const isLast = currentIndex === questions.length - 1;
  const current = questions[currentIndex];

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        attempt_id: attemptId,
        participant_id: participant.id,
        answers: questions.map((q) => ({ question_id: q.id, selected_option: answers[q.id] || null })),
      };
      const { data } = await api.post("/quiz/submit", payload);
      setResult(data);
      setStage("result");
      if (data.passed) setShowCongrats(true);
      await refreshProgress();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-advance (or auto-submit on the last question) when the 60s
  // per-question timer runs out. An unanswered question is graded as
  // incorrect by the backend (selected_option sent as null).
  useEffect(() => {
    if (stage !== "in_progress") return;
    if (questionTimeLeft === 0 && !expiredRef.current) {
      expiredRef.current = true;
      if (isLast) {
        handleSubmit();
      } else {
        setCurrentIndex((i) => Math.min(questions.length - 1, i + 1));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionTimeLeft, stage]);

  const handleRetry = async () => {
    await refreshProgress();
    setResult(null);
    setStage("ready");
  };

  const handleRestartTraining = () => {
    navigate("/training-manual");
  };

  if (stage === "loading") {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-500">Loading...</div>;
  }

  if (stage === "locked") {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center space-y-4">
        <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
          <Lock className="h-7 w-7 text-slate-400" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Q&A Locked</h1>
        <p data-testid={QUIZ.lockedMessage} className="text-slate-600">
          You must complete all Training Manual modules before you can take the Q&A assessment.
        </p>
        <Button onClick={() => navigate("/training-manual")} className="bg-denso-red hover:bg-denso-lightred text-white">
          Go to Training Manual
        </Button>
      </div>
    );
  }

  if (stage === "ready") {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center space-y-5">
        <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <Award className="h-7 w-7 text-denso-red" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">DENSO Product Q&A</h1>
        <p className="text-slate-600">
          40 questions, 60 seconds each. You need <span className="font-bold text-denso-red">90%</span> to earn
          your Digital Certificate. You have{" "}
          <span className="font-bold">{progress?.attempts_remaining ?? 4}</span> attempt(s) remaining in this
          cycle.
        </p>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}
        <Button data-testid={QUIZ.startButton} onClick={startQuiz} className="bg-denso-red hover:bg-denso-lightred text-white h-11 px-8">
          Start Q&A Assessment
        </Button>
      </div>
    );
  }

  if (stage === "in_progress" && current) {
    const selected = answers[current.id];
    const timeWarning = questionTimeLeft <= 10;
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-center justify-between text-sm">
          <span data-testid={QUIZ.progressIndicator} className="font-semibold text-slate-700">
            Question {currentIndex + 1} / {questions.length}
          </span>
          <span
            data-testid={QUIZ.timer}
            className={`flex items-center gap-1.5 font-semibold ${
              timeWarning ? "text-red-600 animate-pulse" : "text-slate-500"
            }`}
          >
            <Clock className="h-4 w-4" /> {questionTimeLeft}s left
          </span>
        </div>
        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-denso-lightred transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-1000 linear ${timeWarning ? "bg-red-500" : "bg-slate-400"}`}
            style={{ width: `${(questionTimeLeft / QUESTION_SECONDS) * 100}%` }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            data-testid={QUIZ.questionCard}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-md border border-slate-200 p-6 space-y-5"
          >
            <span className="text-xs uppercase tracking-widest font-semibold text-denso-red">
              {current.section}
            </span>
            <h2 className="font-semibold text-lg text-slate-900 leading-snug">{current.question}</h2>
            <RadioGroup value={selected || ""} onValueChange={(v) => selectAnswer(current.id, v)}>
              {Object.entries(current.options).map(([key, label]) => (
                <label
                  key={key}
                  htmlFor={`opt-${current.id}-${key}`}
                  className={`flex items-center gap-3 rounded-md border px-4 py-3 cursor-pointer transition-all duration-200 ${
                    selected === key ? "border-denso-red bg-red-50/60" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <RadioGroupItem
                    value={key}
                    id={`opt-${current.id}-${key}`}
                    data-testid={QUIZ.optionRadio(current.id, key)}
                  />
                  <span className="text-sm text-slate-700">{label}</span>
                </label>
              ))}
            </RadioGroup>
          </motion.div>
        </AnimatePresence>

        {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}

        <div className="flex items-center justify-end">
          {!isLast ? (
            <Button
              data-testid={QUIZ.nextQuestionButton}
              disabled={!selected}
              onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
              className="bg-denso-red hover:bg-denso-lightred text-white"
            >
              Next
            </Button>
          ) : (
            <Button
              data-testid={QUIZ.submitButton}
              disabled={!selected || submitting}
              onClick={handleSubmit}
              className="bg-denso-red hover:bg-denso-lightred text-white"
            >
              {submitting ? "Submitting..." : "Submit Assessment"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (stage === "result" && result) {
    if (result.certified && !result.answers) {
      return (
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16 text-center space-y-5">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">You're already certified!</h1>
          <Button onClick={() => navigate("/certificate")} className="bg-denso-red hover:bg-denso-lightred text-white">
            View Certificate
          </Button>
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <Dialog open={showCongrats} onOpenChange={setShowCongrats}>
          <DialogContent data-testid={QUIZ.congratsModal} className="text-center">
            <DialogHeader>
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                <PartyPopper className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-xl font-heading">Congratulations!</DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                You have completed the DENSO Product Training Course with a score of {result.percentage}%!
              </DialogDescription>
            </DialogHeader>
            <Button
              data-testid={QUIZ.viewCertificateButton}
              onClick={() => navigate("/certificate")}
              className="bg-denso-red hover:bg-denso-lightred text-white w-full h-11"
            >
              View My Digital Certificate
            </Button>
          </DialogContent>
        </Dialog>

        <div className={`rounded-md p-6 text-center space-y-2 ${result.passed ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
          {result.passed ? (
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
          ) : (
            <XCircle className="h-10 w-10 text-red-500 mx-auto" />
          )}
          <p data-testid={QUIZ.resultScore} className="text-3xl font-bold text-slate-900">
            {result.score}/{result.total_questions} ({result.percentage}%)
          </p>
          <p className="text-sm text-slate-600">Time taken: {formatTime(result.time_taken_seconds)}</p>
          {!result.passed && !result.reset_required && (
            <p className="text-sm font-medium text-slate-700">
              You need 90% to pass. Attempts remaining: {result.attempts_remaining}
            </p>
          )}
          {result.reset_required && (
            <p className="text-sm font-semibold text-red-700">
              You have used all attempts in this cycle. You must rewatch all training videos before
              retaking the Q&A.
            </p>
          )}
        </div>

        {!result.passed && (
          <div className="flex justify-center gap-3">
            {result.reset_required ? (
              <Button data-testid={QUIZ.restartTrainingButton} onClick={handleRestartTraining} className="bg-denso-red hover:bg-denso-lightred text-white">
                <RotateCcw className="h-4 w-4" /> Restart Training
              </Button>
            ) : (
              <Button data-testid={QUIZ.retryButton} onClick={handleRetry} className="bg-denso-red hover:bg-denso-lightred text-white">
                <RotateCcw className="h-4 w-4" /> Try Again
              </Button>
            )}
          </div>
        )}

        <div className="space-y-3">
          <h3 className="font-semibold text-slate-800">Answer Review</h3>
          {result.answers.map((a) => (
            <div
              key={a.question_id}
              data-testid={QUIZ.resultReviewItem(a.question_id)}
              className={`rounded-md border p-4 text-sm ${a.is_correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}
            >
              <div className="flex items-start gap-2">
                {a.is_correct ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                )}
                <div className="space-y-1">
                  <p className="font-medium text-slate-800">
                    Q{a.question_id}. {a.question_text}
                  </p>
                  <p className="text-slate-600">
                    Your answer:{" "}
                    <span className={a.is_correct ? "text-emerald-700 font-medium" : "text-red-600 font-medium"}>
                      {a.selected_option ? a.options[a.selected_option] : "No answer"}
                    </span>
                  </p>
                  {!a.is_correct && (
                    <p className="text-slate-600">
                      Correct answer: <span className="text-emerald-700 font-medium">{a.options[a.correct_option]}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
